<template>
    <div class="canbus-panel">
        <div class="canbus-header">
            <div>
                <div class="canbus-kicker">Télémétrie véhicule</div>
                <div class="canbus-title">Données CAN bus</div>
            </div>

            <div class="canbus-count">
                {{ availableCount }} mesure{{ availableCount > 1 ? 's' : '' }}
            </div>
        </div>

        <div class="canbus-groups">
            <section v-for="group in visibleGroups" :key="group.title" class="canbus-group">
                <div class="canbus-group-title">
                    {{ group.title }}
                </div>

                <div class="canbus-values">
                    <div v-for="field in group.fields" :key="field.key" class="canbus-value">
                        <div class="canbus-label">
                            {{ field.label }}
                        </div>

                        <div class="canbus-value-content">
                            <span>{{ formatValue(field) }}</span>
                            <span v-if="field.unit" class="canbus-unit">
                                {{ field.unit }}
                            </span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
const FIELD_GROUPS = [
    {
        title: "Conduite",
        fields: [
            { key: "vehicleSpeed", label: "Vitesse véhicule", unit: "km/h" },
            { key: "engineRpm", label: "Régime moteur", unit: "rpm" },
            { key: "engineLoadPct", label: "Charge moteur", unit: "%" },
            { key: "throttlePositionPct", label: "Accélérateur", unit: "%" }
        ]
    },
    {
        title: "Moteur",
        fields: [
            { key: "coolantTemperature", label: "Liquide refroidissement", unit: "°C" },
            { key: "engineOilTemperature", label: "Température huile", unit: "°C" },
            { key: "intakeAirTemperature", label: "Air admission", unit: "°C" },
            { key: "mafAirFlow", label: "Débit d'air", unit: "g/s" },
            { key: "controlModuleVoltage", label: "Tension calculateur", unit: "V" },
            { key: "engineRuntime", label: "Temps moteur", format: "seconds" }
        ]
    },
    {
        title: "Carburant & kilométrage",
        fields: [
            { key: "fuelLevelPct", label: "Niveau carburant", unit: "%" },
            { key: "fuelLevelLiters", label: "Carburant restant", unit: "L" },
            { key: "fuelRate", label: "Consommation instantanée", unit: "L/h" },
            { key: "totalMileage", label: "Kilométrage total", unit: "km" }
        ]
    },
    {
        title: "Diagnostic",
        fields: [
            { key: "dtcCount", label: "Nombre de défauts" },
            { key: "faultCodes", label: "Codes défaut" },
            { key: "distanceWithMil", label: "Distance avec voyant moteur", unit: "km" },
            { key: "distanceSinceDtcClear", label: "Depuis effacement défauts", unit: "km" },
            { key: "timeWithMil", label: "Temps avec voyant moteur", format: "minutes" },
            { key: "hybridBatteryRemainingLifePct", label: "Batterie hybride", unit: "%" }
        ]
    }
];

export default {
    name: "CanbusDataDisplay",

    props: {
        canbusData: {
            type: Object,
            required: true
        }
    },

    computed: {
        visibleGroups() {
            return FIELD_GROUPS
                .map(group => ({
                    ...group,
                    fields: group.fields.filter(field =>
                        this.hasValue(this.canbusData?.[field.key])
                    )
                }))
                .filter(group => group.fields.length);
        },

        availableCount() {
            return this.visibleGroups.reduce(
                (total, group) => total + group.fields.length,
                0
            );
        }
    },

    methods: {
        hasValue(value) {
            return value !== null &&
                value !== undefined &&
                value !== "";
        },

        formatValue(field) {
            const value = this.canbusData?.[field.key];

            if (field.format === "seconds") {
                return this.formatSeconds(value);
            }

            if (field.format === "minutes") {
                return this.formatMinutes(value);
            }

            const numericValue = Number(value);

            if (
                value !== "" &&
                value !== null &&
                value !== undefined &&
                Number.isFinite(numericValue)
            ) {
                return new Intl.NumberFormat("fr-FR", {
                    maximumFractionDigits: 2
                }).format(numericValue);
            }

            return value;
        },

        formatSeconds(value) {
            const seconds = Number(value);

            if (!Number.isFinite(seconds)) {
                return value;
            }

            if (seconds < 60) {
                return `${seconds} s`;
            }

            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);

            if (!hours) {
                return `${minutes} min`;
            }

            return `${hours} h ${minutes} min`;
        },

        formatMinutes(value) {
            const minutes = Number(value);

            if (!Number.isFinite(minutes)) {
                return value;
            }

            if (minutes < 60) {
                return `${minutes} min`;
            }

            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;

            return `${hours} h ${remainingMinutes} min`;
        }
    }
};
</script>

<style scoped>
.canbus-panel {
    margin: 0.45rem 0 0.85rem;
    padding: 1.15rem 1.25rem;
    background: #f8fafc;
    border: 1px solid #e1e7ef;
    border-radius: 10px;
    box-shadow: inset 3px 0 0 #4361ee;
}

.canbus-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.canbus-kicker {
    margin-bottom: 0.1rem;
    color: #778197;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.canbus-title {
    color: #222b3a;
    font-size: 1rem;
    font-weight: 650;
}

.canbus-count {
    flex-shrink: 0;
    padding: 0.3rem 0.65rem;
    color: #536078;
    background: #ffffff;
    border: 1px solid #dce3ed;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
}

.canbus-groups {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 1rem 1.4rem;
}

.canbus-group-title {
    padding-bottom: 0.4rem;
    margin-bottom: 0.35rem;
    color: #6d778b;
    border-bottom: 1px solid #e1e6ee;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.canbus-values {
    display: flex;
    flex-direction: column;
}

.canbus-value {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.34rem 0;
}

.canbus-label {
    color: #667085;
    font-size: 0.79rem;
}

.canbus-value-content {
    color: #222b3a;
    font-size: 0.82rem;
    font-weight: 650;
    text-align: right;
    white-space: nowrap;
}

.canbus-unit {
    margin-left: 0.15rem;
    color: #8a94a6;
    font-size: 0.72rem;
    font-weight: 500;
}

@media (max-width: 768px) {
    .canbus-panel {
        padding: 1rem;
    }

    .canbus-groups {
        grid-template-columns: 1fr;
    }
}
</style>